<template>
  <div
    v-if="relatedGames.length > 0"
    class="related-games-selector"
  >
    <div class="related-games-checkboxes">
      <label
        v-for="game in relatedGames"
        :key="game.slug"
        class="related-game-checkbox"
        :class="{ 'disabled': isProcessing }"
      >
        <input
          type="checkbox"
          :value="game.slug"
          :checked="selectedGames.includes(game.slug)"
          :disabled="isProcessing"
          @change="handleToggle(game.slug)"
        >
        <span class="checkbox-label">{{ game.name }}</span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults, defineProps, defineEmits } from 'vue';
import { useRelatedGames } from '../composables/useRelatedGames';

interface Props {
  currentGameSlug: string;
  selectedGames: string[];
  isProcessing?: boolean;
}

interface Emits {
  (e: 'update:selectedGames', games: string[]): void;
}

const props = withDefaults(defineProps<Props>(), {
  isProcessing: false,
});

const emit = defineEmits<Emits>();
const { getRelatedGamesSync } = useRelatedGames();

const relatedGames = computed(() => {
  return getRelatedGamesSync(props.currentGameSlug);
});

const handleToggle = (gameSlug: string) => {
  const newSelection = [...props.selectedGames];
  const index = newSelection.indexOf(gameSlug);
  
  if (index > -1) {
    newSelection.splice(index, 1);
  } else {
    newSelection.push(gameSlug);
  }
  
  emit('update:selectedGames', newSelection);
};
</script>

<script lang="ts">
export default {
  name: 'RelatedGamesSelector',
};
</script>

<style scoped>
.related-games-selector {
  margin-top: 8px;
  padding: 0;
}

.related-games-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 6px;
}

.related-game-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: transparent;
  /* border-radius: 20px; */
  border: 1px solid var(--colour-surface-cont);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.related-game-checkbox:hover:not(.disabled) {
  background: var(--bg-hover);
}

.related-game-checkbox.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.related-game-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--colour-orange-button);
  flex-shrink: 0;
}

.related-game-checkbox.disabled input[type="checkbox"] {
  cursor: not-allowed;
}

.checkbox-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--colour-base-text-colour);
  flex: 1;
}

.related-game-checkbox input[type="checkbox"]:checked + .checkbox-label {
  color: var(--colour-orange-button);
  font-weight: 600;
}

@media (max-width: 768px) {
  .related-games-selector {
    margin-top: 8px;
    padding: 0;
  }

  .related-game-checkbox {
    padding: 4px 8px;
  }

  .checkbox-label {
    font-size: 12px;
  }
}
</style>

